import { useCallback, useState } from 'react';

import { wait } from '@change-corgi/core/async';
import { GQLResponsePerimeterXChallengeError } from '@change-corgi/core/gql';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { CancellationSource } from '#src/app/shared/constants';

import { cancelMembership } from './api';

export type CancelMembershipResult = [
	/**
	 * `cancelled` is true once the subscription is cancelled, including when it was already cancelled
	 * before this request; `alreadyCancelled` is true only in that case, so callers can show the
	 * cancelled state without recording a new cancellation.
	 */
	{ cancelled: boolean; alreadyCancelled: boolean; cancelError: string | undefined },
	(id: string, token?: string) => Promise<void>,
];

export function useCancelMembership({
	cancellationSource,
}: {
	cancellationSource?: CancellationSource;
} = {}): CancelMembershipResult {
	const utilityContext = useUtilityContext();

	const [cancelled, setCancelled] = useState(false);
	const [alreadyCancelled, setAlreadyCancelled] = useState(false);
	const [cancelError, setCancelError] = useState<string | undefined>(undefined);

	const cancel = useCallback(
		async (id: string, token?: string) => {
			try {
				const { alreadyCancelled: wasAlreadyCancelled } = await cancelMembership(
					id,
					cancellationSource,
					token,
					utilityContext,
				);
				setCancelError(undefined);

				// TODO: Remove the delay after addressing cancellation webhook delay https://change.atlassian.net/browse/CHANGE-50777
				// Awaited so the returned promise stays pending until `cancelled` flips, letting callers keep their
				// submit button disabled for the whole cancellation.
				await wait(1500);
				setAlreadyCancelled(wasAlreadyCancelled);
				setCancelled(true);
			} catch (e) {
				if (e instanceof GQLResponsePerimeterXChallengeError) throw e;

				const message = e instanceof Error ? e.message : String(e);
				setCancelError(`An error occurred trying to cancel the membership: ${message}`);
			}
		},
		[cancellationSource, utilityContext],
	);

	return [{ cancelled, alreadyCancelled, cancelError }, cancel];
}
